<template>
  <div
    v-if="open"
    class="tc-events-inspector"
    data-testid="events-inspector"
    role="complementary"
    aria-label="Triage"
  >
    <!-- Header -->
    <div class="tc-inspector-header">
      <span class="tc-inspector-title">Triage</span>
      <button
        type="button"
        class="tc-btn-icon"
        aria-label="Close triage"
        data-testid="events-inspector-close"
        @click="emit('close')"
      >
        <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <div
      v-if="newEvents > 0"
      class="d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-bottom small"
      role="status"
      data-testid="inspector-new-events"
    >
      <span>{{ newEvents }} new {{ newEvents === 1 ? 'event' : 'events' }} since your last update</span>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        data-testid="inspector-update-draft"
        @click="emit('updateDraft')"
      >Update draft</button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="tc-events-empty">
      <div class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></div>
      <span class="ms-2">Loading events…</span>
    </div>

    <!-- Calendar not linked / reauth required / no sources connected -->
    <div v-else-if="showNoCalendar" class="tc-events-empty">
      <span class="tc-events-empty-message">No calendar connected &mdash; manage in </span><button
        type="button"
        class="btn btn-link btn-sm tc-events-empty-sources-link"
        data-testid="events-inspector-open-sources"
        @click="openSources"
      >Sources</button>
    </div>

    <!-- Generic error -->
    <div v-else-if="isError" class="tc-events-empty" role="alert">
      Could not load events: {{ errorMessage }}
    </div>

    <!-- Main scrollable body: judgment sections + feed -->
    <div v-else class="tc-events-scroll tc-scroll">

      <!-- Section 1: Needs input — rendered from the draft's needsInput items
           (not the calendar join), so degraded items still surface with a
           reconnect hint. Resolved/dismissed cards stay visible + reversible. -->
      <div
        v-if="needsInputItems.length > 0"
        class="tc-inspector-section tc-inspector-section--needs-input"
        data-testid="inspector-needs-input-section"
      >
        <button
          type="button"
          class="tc-inspector-section-header"
          :aria-expanded="needsInputOpen"
          data-testid="inspector-needs-input-toggle"
          @click="needsInputOpen = !needsInputOpen"
        >
          <span class="tc-inspector-section-chevron" aria-hidden="true">{{ needsInputOpen ? '▾' : '▸' }}</span>
          <span>Pending</span>
          <span class="tc-inspector-section-count tc-inspector-section-count--med">{{ needsInputOutstanding }}</span>
          <span class="tc-inspector-section-sources">
            <span v-for="s in needsInputSourceBreakdown" :key="s.id" class="tc-inspector-source-chip">
              <SourceBadge :source="s.id" variant="compact" /><span class="tc-inspector-source-chip-count">{{ s.count }}</span>
            </span>
          </span>
        </button>
        <ul v-if="needsInputOpen" class="tc-events-list">
          <template v-for="entry in needsInputEntries" :key="entry.key">

          <!-- A recurring series: one card, one decision. Collapsed it shows
               how many; open it lists every event so the group can be checked
               before it is committed to. -->
          <li
            v-if="entry.kind === 'group'"
            class="tc-event-group"
            :class="{ 'is-open': openGroups.has(entry.key) }"
            :data-testid="`event-group-${entry.key}`"
          >
            <div class="tc-event-group-card">
              <!-- role=button rather than <button>: the meta row carries a link
                   to the source, which a button element can't contain. Mirrors
                   the single card below, which is a role=button holding its own
                   dismiss control. -->
              <div
                class="tc-event-card tc-event-card--needs-input tc-event-group-head"
                role="button"
                tabindex="0"
                :aria-expanded="openGroups.has(entry.key)"
                :data-testid="`event-group-toggle-${entry.key}`"
                @click="toggleGroup(entry.key)"
                @keydown.enter="toggleGroup(entry.key)"
                @keydown.space.prevent="toggleGroup(entry.key)"
              >
                <span class="tc-event-card-meta">
                  <SourceBadge v-if="itemSourceBadge(entry.items[0])" :source="itemSourceBadge(entry.items[0])!" variant="compact" />
                  <span class="tc-event-card-time">{{ groupSpan(entry.items) }}</span>
                  <SourceLink :link="linkFor(entry.items[0].eventId)" />
                  <span class="tc-event-card-spacer"></span>
                  <svg class="tc-event-group-chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="tc-event-group-title-row">
                  <span class="tc-event-card-title">{{ entry.items[0].subject }}</span>
                  <span class="tc-event-group-count" :data-testid="`event-group-count-${entry.key}`">
                    {{ entry.items.length }} events
                  </span>
                </span>
                <span v-if="sharedReason(entry.items)" class="tc-event-card-filter-reason">{{ sharedReason(entry.items) }}</span>
              </div>

              <div v-if="openGroups.has(entry.key)" class="tc-event-group-body">
                <ul class="tc-event-group-members">
                  <li
                    v-for="m in entry.items"
                    :key="m.eventId"
                    class="tc-event-group-member"
                    :data-testid="`event-group-member-${m.eventId}`"
                  >
                    <span class="tc-event-group-member-day">{{ m.start ? formatDate(m.start) : '—' }}</span>
                    <span class="tc-event-group-member-time">{{ m.start ? formatTime(m.start) : '' }}</span>
                    <span class="tc-event-card-spacer"></span>
                    <span class="tc-event-group-member-hours">{{ m.hours ? `${m.hours}h` : '' }}</span>
                  </li>
                </ul>
                <!-- The series shares one set of source facts. -->
                <SourceFactsRow :facts="factsFor(entry.items[0].eventId)" />
                <div class="tc-event-group-foot">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    :data-testid="`event-group-attribute-${entry.key}`"
                    @click="onGroupAttribute(entry.items, $event)"
                  >Pick project for {{ entry.items.length }}</button>
                  <span class="tc-event-card-spacer"></span>
                  <button
                    type="button"
                    class="btn btn-link btn-sm tc-event-group-ungroup"
                    :data-testid="`event-group-ungroup-${entry.key}`"
                    @click="onUngroup(entry.key)"
                  >Ungroup</button>
                </div>
              </div>
            </div>
          </li>

          <li
            v-else
            :key="entry.item.eventId"
            class="tc-event-card tc-event-card--needs-input"
            :class="{ 'tc-event-card--dismissed': isDismissed(entry.item.eventId) }"
            :data-testid="`event-row-${entry.item.eventId}`"
            role="button"
            tabindex="0"
            @click="onNeedsInputClick(entry.item.eventId, $event)"
            @keydown.enter="onNeedsInputClick(entry.item.eventId, $event)"
            @keydown.space.prevent="onNeedsInputClick(entry.item.eventId, $event)"
          >
            <div class="tc-event-card-meta">
              <ConfidenceBar
                v-if="isDismissed(entry.item.eventId)"
                state="dismissed"
                variant="dot"
                :data-testid="`event-conf-${entry.item.eventId}`"
              />
              <SourceBadge v-if="itemSourceBadge(entry.item)" :source="itemSourceBadge(entry.item)!" variant="compact" />
              <span class="tc-event-card-time">
                <template v-if="entry.item.start && formatDate(entry.item.start)">{{ formatDate(entry.item.start) }} · {{ formatTime(entry.item.start) }}</template>
                <template v-if="entry.item.hours">{{ entry.item.start && formatDate(entry.item.start) ? ' · ' : '' }}{{ entry.item.hours }}h</template>
              </span>
              <!-- Where the item lives in the source: provenance, so it sits with
                   the badge and the timestamp rather than among the facts. -->
              <SourceLink v-if="!isDegraded(entry.item)" :link="linkFor(entry.item.eventId)" />
              <span class="tc-event-card-spacer"></span>
              <span class="tc-event-card-actions">
                <button
                  type="button"
                  class="tc-btn-icon tc-event-card-action tc-event-card-action--dismiss"
                  :class="{ 'is-active': isDismissed(entry.item.eventId) }"
                  :aria-pressed="isDismissed(entry.item.eventId)"
                  :aria-label="isDismissed(entry.item.eventId) ? 'Undo dismiss' : 'Dismiss'"
                  :data-testid="`needs-input-dismiss-${entry.item.eventId}`"
                  @click.stop="emit('dismissNeedsInput', entry.item.eventId)"
                >
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  </svg>
                </button>
              </span>
            </div>
            <div class="tc-event-card-title">{{ isDegraded(entry.item) ? (entry.item.hours ? `${entry.item.hours}h` : GONE_FROM_CALENDAR) : (entry.item.subject || GONE_FROM_CALENDAR) }}</div>
            <div v-if="!isDegraded(entry.item) && entry.item.reason" class="tc-event-card-filter-reason" data-testid="needs-input-reason">{{ entry.item.reason }}</div>
            <SourceFactsRow v-if="!isDegraded(entry.item)" :facts="factsFor(entry.item.eventId)" />
            <div v-if="isDegraded(entry.item)" class="tc-event-card-degraded" data-testid="needs-input-degraded">
              <span aria-hidden="true">⚠</span>
              <span>{{ degradedMessage }}</span>
              <button
                v-if="unhealthySource"
                type="button"
                class="btn btn-link btn-sm p-0"
                data-testid="needs-input-reconnect"
                @click.stop="openSources"
              >Reconnect</button>
            </div>
          </li>

          </template>
        </ul>
      </div>

      <!-- Section 2: Skipped — agent skips + relevance exclusions (ADR-0043); collapsed by default (long noise, low value) -->
      <div
        v-if="filteredEvents.length > 0"
        class="tc-inspector-section tc-inspector-section--filtered"
        data-testid="inspector-filtered-section"
      >
        <button
          type="button"
          class="tc-inspector-section-header"
          :aria-expanded="filteredOpen"
          data-testid="inspector-filtered-toggle"
          @click="filteredOpen = !filteredOpen"
        >
          <span class="tc-inspector-section-chevron" aria-hidden="true">{{ filteredOpen ? '▾' : '▸' }}</span>
          <span>Skipped</span>
          <span class="tc-inspector-section-count">{{ filteredEvents.length }}</span>
          <span class="tc-inspector-section-sources">
            <span v-for="s in filteredSourceBreakdown" :key="s.id" class="tc-inspector-source-chip">
              <SourceBadge :source="s.id" variant="compact" /><span class="tc-inspector-source-chip-count">{{ s.count }}</span>
            </span>
          </span>
        </button>
        <!-- Collapsed, the group would say how many were skipped but not why,
             which reads as unexplained loss (#6239). Open, every card carries
             its own reason and this would only repeat them. -->
        <div
          v-if="!filteredOpen"
          class="tc-inspector-section-reasons"
          data-testid="inspector-filtered-reasons"
        >{{ filteredReasonSummary }}</div>
        <ul v-if="filteredOpen" class="tc-events-list">
          <li
            v-for="item in filteredEvents"
            :key="item.event.id"
            class="tc-event-card tc-event-card--filtered"
            :data-testid="`event-row-${item.event.id}`"
          >
            <div class="tc-event-card-meta">
              <SourceBadge v-if="sourceBadge(item.event)" :source="item.event.source" variant="compact" />
              <span class="tc-event-card-time">
                <template v-if="formatDate(item.event.start)">{{ formatDate(item.event.start) }} · </template>
                <template v-if="item.event.isAllDay">All day</template>
                <template v-else>{{ formatTime(item.event.start) }} – {{ formatTime(item.event.end) }}<template v-if="durationHours(item.event.start, item.event.end) !== null"> · {{ durationHours(item.event.start, item.event.end) }}h</template></template>
              </span>
              <SourceLink :link="sourceLink(item.event)" />
              <span class="tc-event-card-spacer"></span>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm py-0"
                :data-testid="`filtered-use-${item.event.id}`"
                @click.stop="onUseFilteredEvent(item, $event)"
              >Use anyway</button>
            </div>
            <div class="tc-event-card-title">{{ item.event.subject }}</div>
            <div class="tc-event-card-filter-reason">{{ item.reason }}</div>
          </li>
        </ul>
      </div>

      <!-- Section 3: the feeds — work the user has dealt with, split by what
           happened to it. Confirmed is still to send; Published this app wrote;
           Duplicate is work Vantagepoint already carries at the same shape
           (#6487). Same card markup for all three, so it is written once. -->
      <div
        v-for="feed in feeds"
        :key="feed.id"
        class="tc-inspector-section tc-inspector-section--feed"
        :data-testid="`inspector-${feed.id}-section`"
      >
        <button
          type="button"
          class="tc-inspector-section-header"
          :aria-expanded="isFeedOpen(feed.id)"
          :data-testid="`inspector-${feed.id}-toggle`"
          @click="toggleFeed(feed.id)"
        >
          <span class="tc-inspector-section-chevron" aria-hidden="true">{{ isFeedOpen(feed.id) ? '▾' : '▸' }}</span>
          <span>{{ feed.label }}</span>
          <span class="tc-inspector-section-count">{{ feed.count }}</span>
          <span class="tc-inspector-section-sources">
            <span v-for="s in feed.sources" :key="s.id" class="tc-inspector-source-chip">
              <SourceBadge :source="s.id" variant="compact" /><span class="tc-inspector-source-chip-count">{{ s.count }}</span>
            </span>
          </span>
        </button>
        <template v-if="isFeedOpen(feed.id)">
        <div
          v-for="group in feed.groups"
          :key="group.dateLabel"
          class="tc-events-day-group"
        >
          <div class="tc-events-day-label">
            <span>{{ group.dateLabel }}</span>
            <span class="tc-events-day-count">{{ group.events.length }} events</span>
          </div>
          <ul class="tc-events-list">
            <li
              v-for="event in group.events"
              :key="event.id"
              class="tc-event-card tc-event-card--attributed"
              :class="{
                'selected': wbsFor(event.id) !== null && wbsFor(event.id) === highlightedWbs,
                'tc-event-card--wbs-highlight': wbsFor(event.id) !== null && wbsFor(event.id) === highlightedWbs,
                'tc-event-card--focused': focusedId === event.id,
                'tc-event-card--confirmed': isConfirmed(event.id),
                'tc-event-card--dismissed': isDismissed(event.id),
              }"
              :data-testid="`event-row-${event.id}`"
              role="button"
              tabindex="0"
              :aria-pressed="wbsFor(event.id) !== null && wbsFor(event.id) === highlightedWbs"
              @click="onEventClick(event.id, feed.id)"
              @keydown.enter="onEventClick(event.id, feed.id)"
              @keydown.space.prevent="onEventClick(event.id, feed.id)"
            >
              <div class="tc-event-card-meta">
                <ConfidenceBar
                  v-if="isConfirmed(event.id) || isDismissed(event.id)"
                  :state="isDismissed(event.id) ? 'dismissed' : 'confirmed'"
                  variant="dot"
                  :data-testid="`event-conf-${event.id}`"
                />
                <SourceBadge v-if="sourceBadge(event)" :source="event.source" variant="compact" />
                <span class="tc-event-card-time">
                  <template v-if="event.isAllDay">All day</template>
                  <template v-else>{{ formatTime(event.start) }} – {{ formatTime(event.end) }}<template v-if="durationHours(event.start, event.end) !== null"> · {{ durationHours(event.start, event.end) }}h</template></template>
                </span>
                <SourceLink :link="sourceLink(event)" />
                <span class="tc-event-card-spacer"></span>
                <!-- Written work carries no decision. Confirm is meaningless on
                     something already sent, and Dismiss would imply an unsend
                     this app cannot do. A Duplicate keeps one action, because
                     the shape match is a guess the user may need to overrule. -->
                <span v-if="feed.id === 'feed'" class="tc-event-card-actions">
                  <button
                    type="button"
                    class="tc-btn-icon tc-event-card-action tc-event-card-action--confirm"
                    :class="{ 'is-active': isConfirmed(event.id) }"
                    :aria-pressed="isConfirmed(event.id)"
                    :aria-label="isConfirmed(event.id) ? 'Undo confirm' : 'Confirm event'"
                    title="Confirm (C)"
                    :data-testid="`event-confirm-${event.id}`"
                    @click.stop="emit('confirmEvent', event.id)"
                  >
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="tc-btn-icon tc-event-card-action tc-event-card-action--dismiss"
                    :class="{ 'is-active': isDismissed(event.id) }"
                    :aria-pressed="isDismissed(event.id)"
                    :aria-label="isDismissed(event.id) ? 'Undo dismiss' : 'Dismiss event'"
                    title="Dismiss (D)"
                    :data-testid="`event-dismiss-${event.id}`"
                    @click.stop="emit('dismissEvent', event.id)"
                  >
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                  </button>
                </span>
                <span v-else-if="feed.id === 'duplicate'" class="tc-event-card-actions">
                  <button
                    type="button"
                    class="btn btn-link btn-sm tc-event-group-ungroup"
                    :data-testid="`duplicate-use-${event.id}`"
                    @click.stop="emit('useAnyway', event.id)"
                  >Use anyway</button>
                </span>
                <!-- Published is this app's own record, not a reading of VP.
                     When the work is no longer there, this is the way back. -->
                <span v-else-if="feed.id === 'published'" class="tc-event-card-actions">
                  <button
                    type="button"
                    class="btn btn-link btn-sm tc-event-group-ungroup"
                    :data-testid="`published-readd-${event.id}`"
                    @click.stop="emit('useAnyway', event.id)"
                  >Add again</button>
                </span>
                <span v-else-if="feed.id === 'dismissed'" class="tc-event-card-actions">
                  <button
                    type="button"
                    class="btn btn-link btn-sm tc-event-group-ungroup"
                    :data-testid="`event-dismiss-${event.id}`"
                    @click.stop="emit('dismissEvent', event.id)"
                  >Undo</button>
                </span>
              </div>
              <div class="tc-event-card-title">{{ event.subject }}</div>
              <div
                v-if="chipFor(event.id)"
                class="tc-event-suggested-chip"
                :title="`${chipFor(event.id)!.wbs1} · ${chipFor(event.id)!.wbs1Name}`"
              >
                <span class="tc-event-suggested-wbs">{{ chipFor(event.id)!.wbs1 }}</span>
                <span class="tc-event-suggested-name">{{ chipFor(event.id)!.wbs1Name }}</span>
              </div>
            </li>
          </ul>
        </div>
        </template>
      </div>

      <!-- Empty state (no events at all, after load) -->
      <div v-if="groups.length === 0 && needsInputItems.length === 0 && filteredEvents.length === 0" class="tc-events-empty">
        No events in this period.
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getCalendarEvents } from '../api/calendar';
import type { SourceEvent, SourceId, CalendarEventsResponse } from '../api/calendar';
import type { DraftResponse, NeedsInputItem, WrittenState } from '../api/draft';
import { ApiClientError } from '../api/client';
import { getSources } from '../api/sources';
import { useSourcesStore } from '../stores/sources';
import { useTimezoneStore } from '../stores/timezone';
import {
  buildNeedsInputEntries, groupHours, queuedNeedsInput, sharedReason,
  type NeedsInputEntry,
} from './needsInputGroups';
import { SOURCE_META, type SourceMeta } from '../connectors/registry';
import { sourceFacts, sourceLink, type SourceFacts, type SourceLink as SourceLinkModel } from './sourceFacts';
import SourceBadge from './ui/SourceBadge.vue';
import SourceFactsRow from './ui/SourceFactsRow.vue';
import SourceLink from './ui/SourceLink.vue';
import ConfidenceBar from './ui/ConfidenceBar.vue';

const props = defineProps<{
  open: boolean;
  draft: DraftResponse | null;
  // Overlay-aware draft (reconciled) — drives the attributed feed and the
  // event→row joins, so a resolved needs-input item appears under Attributed
  // and a re-targeted event maps to its new row.
  effectiveDraft?: DraftResponse | null;
  highlightedWbs?: string;
  calendarFrom?: string;
  calendarTo?: string;
  // Quick-triage (C3): keyboard focus ring + per-event overlay state, driven by
  // the parent's triage store overlay.
  focusedId?: string | null;
  confirmedIds?: Set<string>;
  dismissedIds?: Set<string>;
  // A modal (wizard / cell card) is open over the drawer — Escape belongs to it,
  // so the drawer ignores Escape until it closes.
  escapeSuppressed?: boolean;
  // Source events the saved draft hasn't seen, from the new-events check.
  newEvents: number;
}>();

const emit = defineEmits<{
  close: [];
  updateDraft: [];
  // Where the clicked card's work lives: an Attributed card still owns a draft
  // row; a Published or Duplicate one points at the VP row it produced (#6487).
  selectWbs: [wbs: string | null, scope?: 'draft' | 'vp'];
  confirmEvent: [eventId: string];
  // Overrule the in-VP shape match — this work is separate, put it back.
  useAnyway: [eventId: string];
  dismissEvent: [eventId: string];
  // Ordered attributed-event ids, so the parent's keyboard nav walks the same
  // sequence the feed renders.
  attributedIds: [ids: string[]];
  // Clicking a card navigates to its point of manipulation — an attributed
  // card scrolls/flashes the owning grid row and the event's specific day
  // cell; a needs-input card opens the resolver anchored to the card.
  scrollToRow: [wbs: string, dayIndex?: number, scope?: 'draft' | 'vp'];
  selectNeedsInput: [eventId: string, anchor: HTMLElement];
  // A whole group resolved in one pass — the wizard opens once and its confirm
  // writes the same leaf to every event, each keeping its own day and hours.
  selectNeedsInputGroup: [items: NeedsInputItem[], anchor: HTMLElement];
  dismissNeedsInput: [eventId: string];
  // An agent-skipped event the user chose to keep — opens the resolver seeded
  // with the event's synthesized hours/day so it can be attributed like a
  // needs-input pick.
  promoteFiltered: [item: NeedsInputItem, anchor: HTMLElement];
}>();

function isConfirmed(eventId: string): boolean {
  return props.confirmedIds?.has(eventId) ?? false;
}

function isDismissed(eventId: string): boolean {
  return props.dismissedIds?.has(eventId) ?? false;
}

const sourcesStore = useSourcesStore();
const tzStore = useTimezoneStore();

function openSources(): void {
  sourcesStore.openModal();
}

// ---------------------------------------------------------------------------
// Collapsible section state (Item 1, ux-batch-2026-07) — "Skipped"
// starts collapsed since it's long noise; the other two default open.
// ---------------------------------------------------------------------------
const needsInputOpen = ref(true);
const filteredOpen = ref(false);

// ---------------------------------------------------------------------------
// Escape key
// ---------------------------------------------------------------------------
function onKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.open && !props.escapeSuppressed) emit('close');
}

onMounted(() => document.addEventListener('keydown', onKeyDown));
onUnmounted(() => document.removeEventListener('keydown', onKeyDown));

// ---------------------------------------------------------------------------
// Calendar events query — only fires once the drawer is first opened
// ---------------------------------------------------------------------------
const eventsQuery = useQuery<CalendarEventsResponse, ApiClientError>({
  queryKey: computed(() => ['calendar-events', props.calendarFrom ?? '', props.calendarTo ?? '', tzStore.selectedZone]),
  queryFn: () => getCalendarEvents(props.calendarFrom, props.calendarTo, tzStore.selectedZone),
  enabled: computed(() => props.open),
  retry: false,
  staleTime: 60_000,
});

// Sources aggregate — shares the ['sources'] cache with Home.vue, so the
// empty-state lights up whenever zero sources are connected (ADR-0029
// amendment §B), not only on a calendar-specific 412.
const sourcesQuery = useQuery({
  queryKey: ['sources'],
  queryFn: getSources,
  staleTime: 30_000,
  retry: false,
});

const noSourcesConnected = computed(() => {
  const s = sourcesQuery.data.value?.sources ?? [];
  return s.length > 0 && s.every(x => x.status !== 'connected');
});

const isLoading = computed(() => eventsQuery.isLoading.value);
const isError = computed(() => eventsQuery.isError.value && !isNotLinked.value && !isReauthRequired.value);

const showNoCalendar = computed(() => isNotLinked.value || isReauthRequired.value || noSourcesConnected.value);

const isNotLinked = computed(() => {
  const err = eventsQuery.error.value;
  if (!(err instanceof ApiClientError)) return false;
  return err.status === 412 && err.code === 'msgraph_not_linked';
});

const isReauthRequired = computed(() => {
  const err = eventsQuery.error.value;
  if (!(err instanceof ApiClientError)) return false;
  return err.status === 412 && err.code === 'msgraph_reauth_required';
});

const errorMessage = computed(() => {
  const err = eventsQuery.error.value;
  if (err instanceof ApiClientError) return err.message;
  return 'Unknown error';
});

const allEvents = computed(() => eventsQuery.data.value?.events ?? []);

// A needs-input item carries the engine's judgment, not the source's facts, so
// the card joins back to the live event for its tags, deep link and attendance
// (Items 7 + 12). A rehydrated item whose event left the read joins to nothing
// and shows no facts row.
const eventsById = computed((): Map<string, SourceEvent> =>
  new Map(allEvents.value.map(e => [e.id, e])));

function factsFor(eventId: string): SourceFacts | null {
  return sourceFacts(eventsById.value.get(eventId), 'card');
}

function linkFor(eventId: string): SourceLinkModel | null {
  return sourceLink(eventsById.value.get(eventId));
}

// ---------------------------------------------------------------------------
// eventId → wbs mapping derived from draft.rows
// ---------------------------------------------------------------------------
// Rows for the event→row joins: the reconciled draft when supplied (includes
// resolved picks and re-targets), else the raw engine draft.
const joinRows = computed(() => (props.effectiveDraft ?? props.draft)?.rows ?? []);

const wbsByEventId = computed((): Map<string, string> => {
  const map = new Map<string, string>();
  for (const row of joinRows.value) {
    for (const ce of row.contributingEvents) {
      map.set(ce.eventId, row.target.wbs1);
    }
  }
  return map;
});

// eventId → period-relative day, so a card click can flash the exact cell.
const dayIndexByEventId = computed((): Map<string, number> => {
  const map = new Map<string, number>();
  for (const row of joinRows.value) {
    for (const ce of row.contributingEvents) {
      map.set(ce.eventId, ce.dayIndex);
    }
  }
  return map;
});

// eventId → wbs1Name mapping for the chip
const wbs1NameByEventId = computed((): Map<string, string> => {
  const map = new Map<string, string>();
  for (const row of joinRows.value) {
    for (const ce of row.contributingEvents) {
      map.set(ce.eventId, row.target.wbs1Name);
    }
  }
  return map;
});

const needsInputSet = computed((): Set<string> => {
  return new Set(needsInputItems.value.map(i => i.eventId));
});

const filteredReasonByEventId = computed((): Map<string, string> => {
  const map = new Map<string, string>();
  if (!props.draft) return map;
  for (const f of props.draft.filteredOut) {
    map.set(f.eventId, f.reason);
  }
  return map;
});

function wbsFor(eventId: string): string | null {
  return wbsByEventId.value.get(eventId) ?? null;
}

interface ProjectChip { wbs1: string; wbs1Name: string; }

function chipFor(eventId: string): ProjectChip | null {
  const wbs1 = wbsByEventId.value.get(eventId);
  if (!wbs1) return null;
  return { wbs1, wbs1Name: wbs1NameByEventId.value.get(eventId) ?? '' };
}

// ---------------------------------------------------------------------------
// Section 1: needs-input items straight from the draft — degraded items (no
// resolvable event on a fresh read) surface here too. Chronological; items
// with no parseable start (degraded) last.
// ---------------------------------------------------------------------------
// Outstanding + dismissed items (dismissed stay here for the undo click);
// a resolved item leaves the queue and shows in the Attributed feed instead.
const needsInputItems = computed((): NeedsInputItem[] => {
  const ts = (start?: string): number => {
    const t = start ? new Date(start).getTime() : NaN;
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return queuedNeedsInput(props.draft, props.effectiveDraft ?? null, isConfirmed)
    .map(hydrateFromEvent)
    .sort((a, b) => ts(a.start) - ts(b.start));
});

// A re-queued item carries only what the overlay stores — never the subject,
// which is the source's to hold. The live read supplies what the card renders.
function hydrateFromEvent(item: NeedsInputItem): NeedsInputItem {
  if (item.subject) return item;
  const event = eventsById.value.get(item.eventId);
  if (!event) return item;
  return { ...item, subject: event.subject, start: event.start, source: event.source };
}

// Items still awaiting a decision — the section's count badge.
const needsInputOutstanding = computed((): number =>
  needsInputItems.value.filter(i => !isDismissed(i.eventId)).length,
);

// ── Grouped needs-input ──────────────────────────────────────────────────────
// Same source, same subject collapses to one card. The group is all-or-nothing
// by design: its members share a subject by construction, so per-member
// selection would offer control over a set the card has just called uniform.
// A member that doesn't belong means the group is wrong — that's Ungroup.
// Both are view state over the draft currently loaded, not saved state.
// A group is a suggestion the engine's output implies; dissolving one rejects
// it now. Reload, change period or regenerate and the draft is recomputed —
// re-offering the suggestion is what "recompute" means, so they reset with it.
const openGroups = ref(new Set<string>());
const ungrouped = ref(new Set<string>());

watch(() => props.draft?.generatedAt, () => {
  openGroups.value = new Set();
  ungrouped.value = new Set();
});

const needsInputEntries = computed((): NeedsInputEntry[] =>
  buildNeedsInputEntries(needsInputItems.value, isDismissed, ungrouped.value),
);

function toggleGroup(key: string): void {
  const next = new Set(openGroups.value);
  if (!next.delete(key)) next.add(key);
  openGroups.value = next;
}

function groupSpan(items: NeedsInputItem[]): string {
  const days = items.map(i => (i.start ? formatDate(i.start) : '')).filter(Boolean);
  const span = days.length > 1 && days[0] !== days[days.length - 1]
    ? `${days[0]}–${days[days.length - 1]}`
    : days[0] ?? '';
  const hours = groupHours(items);
  return [span, hours ? `${hours}h total` : ''].filter(Boolean).join(' · ');
}

function onGroupAttribute(items: NeedsInputItem[], e: Event): void {
  emit('selectNeedsInputGroup', items, e.currentTarget as HTMLElement);
}

function onUngroup(key: string): void {
  ungrouped.value = new Set(ungrouped.value).add(key);
  const next = new Set(openGroups.value);
  next.delete(key);
  openGroups.value = next;
}

// The backend's exact sentinel for "this event no longer resolves on a fresh
// read" (DraftMapper.Rehydrate).
const GONE_FROM_CALENDAR = 'Event no longer in your calendar';

function isDegraded(item: NeedsInputItem): boolean {
  return item.subject === '(event not found)';
}

const unhealthySource = computed(() =>
  (props.draft?.sources ?? []).find((s) => s.status !== 'connected'),
);

const degradedMessage = computed((): string =>
  unhealthySource.value
    ? 'Source unavailable — reconnect to see details.'
    : "This event's details are no longer available (it may have been deleted or moved).",
);

function itemSourceBadge(item: NeedsInputItem): SourceId | null {
  const src = item.source as SourceId | undefined;
  return src && SOURCE_META[src] ? src : null;
}

// ---------------------------------------------------------------------------
// Section 2: filtered-out events (paired with their reason)
// ---------------------------------------------------------------------------
interface FilteredItem { event: SourceEvent; reason: string; }

// Skipped events still awaiting a decision. A promoted skip (confirmed via
// "Use anyway") drops out of this set and surfaces in the Attributed feed —
// mirrors how a resolved needs-input item behaves.
const activeFilteredIds = computed((): Set<string> => {
  const s = new Set<string>();
  for (const e of allEvents.value) {
    if (filteredReasonByEventId.value.has(e.id) && !isConfirmed(e.id)) s.add(e.id);
  }
  return s;
});

const filteredEvents = computed((): FilteredItem[] => {
  return sortByStart(allEvents.value.filter(e => activeFilteredIds.value.has(e.id)))
    .map(e => ({ event: e, reason: filteredReasonByEventId.value.get(e.id)! }));
});

// Why the group was skipped, commonest reason first. Two are enough to
// characterise the group; beyond that the count says there is more to see.
const filteredReasonSummary = computed((): string => {
  const counts = new Map<string, number>();
  for (const f of filteredEvents.value) counts.set(f.reason, (counts.get(f.reason) ?? 0) + 1);
  const ordered = [...counts].sort((a, b) => b[1] - a[1]);
  const shown = ordered.slice(0, 2).map(([reason, n]) => (n > 1 ? `${reason} (${n})` : reason));
  const rest = ordered.length - shown.length;
  return rest > 0 ? `${shown.join(' · ')} · +${rest} more` : shown.join(' · ');
});

// ---------------------------------------------------------------------------
// Source badge — per-card provenance, every source (the grid carries none;
// the triage cards are where "which system fed this" is answered honestly).
// ---------------------------------------------------------------------------
function sourceBadge(event: SourceEvent): SourceMeta | null {
  return SOURCE_META[event.source] ?? null;
}

// ---------------------------------------------------------------------------
// Section 3: attributed events (day-grouped, excludes needs-input + filtered)
// ---------------------------------------------------------------------------
interface EventGroup { dateLabel: string; events: SourceEvent[]; }

const groups = computed((): EventGroup[] => {
  const attributed = sortByStart(allEvents.value.filter(
    e => wbsByEventId.value.has(e.id)
      && !needsInputSet.value.has(e.id)
      && !activeFilteredIds.value.has(e.id),
  ));
  const map = new Map<string, SourceEvent[]>();
  for (const e of attributed) {
    const d = new Date(e.start);
    const label = isNaN(d.getTime())
      ? 'Unknown date'
      : dayHeading(d);
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(e);
  }
  return Array.from(map.entries()).map(([dateLabel, events]) => ({ dateLabel, events }));
});

// What the app already knows about each event's work, keyed by event id. The
// server marks both shapes — a row's day cell and a held card — so read both.
const writtenByEventId = computed((): Map<string, WrittenState> => {
  const m = new Map<string, WrittenState>();
  const d = props.effectiveDraft ?? props.draft;
  for (const row of d?.rows ?? []) {
    const byDay = new Map(row.days.map(x => [x.dayIndex, x.written]));
    for (const ce of row.contributingEvents) {
      const w = byDay.get(ce.dayIndex);
      if (w) m.set(ce.eventId, w);
    }
  }
  for (const ni of d?.needsInput ?? []) if (ni.written) m.set(ni.eventId, ni.written);
  return m;
});

const dayGroup = (events: SourceEvent[]): EventGroup[] => {
  const map = new Map<string, SourceEvent[]>();
  for (const e of sortByStart(events)) {
    const d = new Date(e.start);
    const label = isNaN(d.getTime())
      ? 'Unknown date'
      : dayHeading(d);
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(e);
  }
  return Array.from(map.entries()).map(([dateLabel, events]) => ({ dateLabel, events }));
};

// Feeds split by what happened to the work. Outstanding work stays in the
// grid's Draft group; anything already recorded lives here instead, so the same
// work is never shown in both places (#6487).
const feeds = computed(() => {
  // Attributed events, plus held cards the app already wrote — the latter never
  // reach `groups` (they have no target), but they are written work and belong
  // in a feed rather than the queue.
  const writtenHeldIds = new Set(
    (props.effectiveDraft ?? props.draft)?.needsInput.filter(i => i.written).map(i => i.eventId) ?? [],
  );
  // Dismissing drops the event from the draft, which also strips the WBS
  // mapping `groups` is keyed on — so the card disappears and takes its own undo
  // control with it. Dismissed work gets a feed of its own to sit in, and is
  // held out of the other three so the split stays a partition.
  const dismissed = allEvents.value.filter(e => isDismissed(e.id));
  const all = [
    ...groups.value.flatMap(g => g.events),
    ...allEvents.value.filter(e => writtenHeldIds.has(e.id)),
  ].filter(e => !isDismissed(e.id));
  const of = (want: WrittenState | null) => all.filter(e => (writtenByEventId.value.get(e.id) ?? null) === want);
  return [
    { id: 'feed', label: 'Confirmed', events: of(null) },
    { id: 'published', label: 'Published', events: of('published') },
    { id: 'duplicate', label: 'Duplicate', events: of('in-vp') },
    { id: 'dismissed', label: 'Dismissed', events: dismissed },
  ]
    .filter(f => f.events.length > 0)
    .map(f => ({
      id: f.id,
      label: f.label,
      count: f.events.length,
      groups: dayGroup(f.events),
      sources: sourceBreakdown(f.events),
    }));
});

// Each feed remembers its own open state. Dismissed starts closed — it is the
// one feed holding work the user has already decided against.
const closedFeeds = ref(new Set<string>(['dismissed']));
const isFeedOpen = (id: string) => !closedFeeds.value.has(id);
function toggleFeed(id: string) {
  const next = new Set(closedFeeds.value);
  if (next.has(id)) next.delete(id); else next.add(id);
  closedFeeds.value = next;
}

// ---------------------------------------------------------------------------
// Per-source breakdown for section headers (Item 1, ux-batch-2026-07) — a
// small SourceBadge + count per contributing source, so provenance is visible
// at a glance without opening the section. Unlike sourceBadge() above (which
// hides the Outlook chip on individual cards), the header breakdown always
// names every source, including Outlook.
// ---------------------------------------------------------------------------
interface SourceBreakdownEntry { id: SourceId; count: number; }

function sourceBreakdown(events: SourceEvent[]): SourceBreakdownEntry[] {
  const counts = new Map<SourceId, number>();
  for (const e of events) {
    // Older/malformed fixtures can omit `source` entirely — skip rather than
    // render a meaningless "unknown" chip in the header.
    if (!e.source) continue;
    counts.set(e.source, (counts.get(e.source) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([id, count]) => ({ id, count }));
}

const needsInputSourceBreakdown = computed(() => {
  const counts = new Map<SourceId, number>();
  for (const i of needsInputItems.value) {
    const src = i.source as SourceId | undefined;
    if (!src) continue;
    counts.set(src, (counts.get(src) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([id, count]) => ({ id, count }));
});
const filteredSourceBreakdown = computed(() => sourceBreakdown(filteredEvents.value.map(f => f.event)));

// Ordered attributed-event ids (feed order) — the sequence J/K navigation walks.
const attributedIds = computed((): string[] =>
  groups.value.flatMap(g => g.events.map(e => e.id)),
);

watch(attributedIds, (ids) => emit('attributedIds', ids), { immediate: true });

// ---------------------------------------------------------------------------
// Click handler — toggle highlight (attributed events only)
// ---------------------------------------------------------------------------
function onEventClick(eventId: string, feedId?: string): void {
  const wbs = wbsFor(eventId);
  // Published and Duplicate work is in Vantagepoint, not the draft — pointing
  // at a draft row would highlight the wrong thing, or nothing at all when the
  // row has left the draft entirely.
  const scope: 'draft' | 'vp' = feedId === 'published' || feedId === 'duplicate' ? 'vp' : 'draft';
  if (!wbs) {
    emit('selectWbs', null);
    return;
  }
  const next = props.highlightedWbs === wbs ? null : wbs;
  // Clearing the highlight has no block to point at, so no scope is sent.
  if (next === null) emit('selectWbs', null);
  else emit('selectWbs', next, scope);
  // Only navigate (scroll/flash) when the card is being highlighted on, not
  // when the click toggles the highlight off.
  if (next) emit('scrollToRow', next, dayIndexByEventId.value.get(eventId), scope);
}

function onNeedsInputClick(eventId: string, e: Event): void {
  emit('selectNeedsInput', eventId, e.currentTarget as HTMLElement);
}

// Hours for an all-day skip promoted to the grid. VP owns the standard day
// (EmployeeInfo.HoursPerDay) and the engine passes it down on the draft, so a
// tenant on a 7.5-hour day gets 7.5 here too.
const standardDayHours = computed(() => props.draft?.standardDayHours ?? 8);

// Period-relative day index for a synthesized pick — the engine computes this
// server-side for needs-input items, but a skipped event never got one. Resolve
// the event's calendar date in the user's selected VP zone (matching the
// engine's EffectiveZone, DraftEngine.DraftAsync) so a promoted timed event
// lands on the same day the server would place it, then diff against the period
// start (date-only). Clamped to the period's first day.
function dayIndexForStart(startIso: string): number {
  const start = new Date(startIso);
  if (isNaN(start.getTime()) || !props.calendarFrom) return 0;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tzStore.selectedZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(start);
  const part = (type: string): number => Number(parts.find(p => p.type === type)!.value);
  const [py, pm, pd] = props.calendarFrom.split('-').map(Number);
  const periodStart = Date.UTC(py, pm - 1, pd);
  const eventDay = Date.UTC(part('year'), part('month') - 1, part('day'));
  return Math.max(0, Math.round((eventDay - periodStart) / 86_400_000));
}

// "Use anyway" on a skipped card — synthesize a needs-input-shaped item (no
// candidate targets, no signature: the agent found no signal to key memory on)
// and hand it to the parent's resolver. All-day skips default to a standard day.
function onUseFilteredEvent(item: FilteredItem, e: Event): void {
  const hours = item.event.isAllDay
    ? standardDayHours.value
    : (durationHours(item.event.start, item.event.end) ?? standardDayHours.value);
  const synthesized: NeedsInputItem = {
    eventId: item.event.id,
    subject: item.event.subject,
    start: item.event.start,
    hours,
    candidateTargets: [],
    dayIndex: dayIndexForStart(item.event.start),
    source: item.event.source,
  };
  emit('promoteFiltered', synthesized, e.currentTarget as HTMLElement);
}

// ---------------------------------------------------------------------------
// Ordering + time / duration formatters
// ---------------------------------------------------------------------------
// Chronological, unparseable starts last. Copies — never mutates the source.
function sortByStart(events: SourceEvent[]): SourceEvent[] {
  const ts = (e: SourceEvent): number => {
    const t = new Date(e.start).getTime();
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return [...events].sort((a, b) => ts(a) - ts(b));
}

function dayHeading(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${mm}/${dd}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function durationHours(start: string, end: string): number | null {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (isNaN(s) || isNaN(e) || e <= s) return null;
  return Math.round(((e - s) / 3_600_000) * 100) / 100;
}
</script>
